<template>
  <ChecklistTableTaskCellWrapper :clickable="isCellClickable"
                                 data-locator="due-date-area"
                                 @click="onCellClick"
  >
    <template v-if="hasDateRelatedData">
      <div class="ChecklistTableTaskDate-due-date column-gap-2 align-start text-text-secondary">
        {{ computedDate }}
        <AppTooltip v-if="displayRemindersIcon">
          <template #activator="{ props }">
            <app-material-icon name="alarm" size="xs" v-bind="props" />
          </template>
          <span>{{ $t(`${TRANSLATION_KEY_PREFIX}.taskRemindersTooltip`) }}</span>
        </AppTooltip>
        <template v-if="displayAdditionalInfo">
          <Popover :disabled="isCurrentUserPm">
            <template #trigger>
              <AppTooltip>
                <template #activator="{ props }">
                  <app-material-icon
                    class="clickable"
                    name="article"
                    size="xs"
                    v-bind="props"
                    @click="onCellClick"
                  />
                </template>
                <span>{{ $t(`${TRANSLATION_KEY_PREFIX}.taskAdditionalInfoTooltip`) }}</span>
              </AppTooltip>
            </template>
            <div class="d-flex flex-column row-gap-2">
              <label for="additionalInfo" class="body-md-bold">{{ $t(`${TRANSLATION_KEY_PREFIX}.additionalInfoLabel`) }}</label>
              <v-textarea v-model="additionalInfoText"
                          id="additionalInfo"
                          readonly
                          variant="outlined"
              />
            </div>
          </Popover>
        </template>
      </div>
    </template>
    <template v-else>
      <ChecklistTableTaskEmptyCell />
    </template>

    <app-modal v-model="isDialogOpen"
               size="md"
               :ok-disabled="isAdditionalInfoTooLong"
               :ok-text="$t('common.save')"
               @cancel="resetAndClose"
               @ok="preparePostDate"
    >
      <template #title>{{ $t(`${TRANSLATION_KEY_PREFIX}.dialogTitle`) }}</template>
      <template #body>
        <v-row class="mt-2">
          <v-col class="text--primary">
            <h4 class="mb-4">{{ $t(`${TRANSLATION_KEY_PREFIX}.pickADate`) }}</h4>
            <AppDatePicker
                v-model="newDate"
                :min="minStartDate"
                class="w-100 ChecklistTableTaskDate-datePicker"
                :placeholder="$t('common.dateUtils.dateFormatHelp')"
                append-inner-icon="calendar_today"
                :error-messages="noDate ? $t(`${TRANSLATION_KEY_PREFIX}.noDate`) : ''"
                @input="noDate = false"
              >
              </AppDatePicker>
          </v-col>
        </v-row>
        <v-row class="text--primary">
          <v-col>
            <div class="mb-3 d-flex align-center">
              <h4>{{ $t(`${TRANSLATION_KEY_PREFIX}.reminders`) }}</h4>
              <AppTooltip max-width="600">
                <template #activator="{ props }">
                  <app-material-icon
                    name="info"
                    class="ml-2"
                    v-bind="props"
                  />
                </template>
                <span class="tooltip-text">{{ $t(`${TRANSLATION_KEY_PREFIX}.remindersText`)  }}</span>
              </AppTooltip>
            </div>
          </v-col>
        </v-row>
        <v-row v-if="newReminders.length > 0">
          <v-col>
            <div
              v-for="(reminder, index) in newReminders"
              :key="`reminder-${index}`"
              class="my-4"
            >
              <div class="d-flex align-center my-2">
                <AppTextField
                  v-model.number="reminder.daysBefore"
                  style="max-width: 146px"
                  :disabled="reminder.sent === 1"
                  type="number"
                  min="0"
                  autofocus
                  :suffix="$t(`${TRANSLATION_KEY_PREFIX}.reminderDaysBefore`, reminder.daysBefore)"
                  class="ChecklistTableTaskDate-dateReminderPicker mr-2"
                  :error="reminder.errorWrongValue || reminder.errorBackToFuture || reminder.errorAfterEvent"
                  @blur="validateReminder(reminder)"
                  @change="validateReminder(reminder)"
                />
                <app-button
                  type="icon"
                  color="#d8d8d8"
                  @click="removeReminder(index)"
                >
                  <app-material-icon name="close" size="xs" />
                </app-button>
              </div>
              <div v-show="reminder.error"
                   :key="`reminderError-${index}`"
                   class="detail-md-regular text-text-danger"
              >
                <p v-show="reminder.errorWrongValue">{{ $t(`${TRANSLATION_KEY_PREFIX}.lblReminderWrongData`) }}</p>
                <p v-show="reminder.errorBackToFuture">{{ $t(`${TRANSLATION_KEY_PREFIX}.lblReminderInThePast`) }}</p>
                <p v-show="reminder.errorAfterEvent">{{ $t(`${TRANSLATION_KEY_PREFIX}.lblReminderNegativeValue`) }}</p>
              </div>
            </div>
          </v-col>
        </v-row>
        <v-row v-else>
          <v-col>
            <p>{{ $t(`${TRANSLATION_KEY_PREFIX}.noReminder`) }}</p>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <app-button
              type="outlined"
              variant="neutral"
              @click="addNewReminder"
            >
              <app-material-icon name="add" />
              {{ $t(`${TRANSLATION_KEY_PREFIX}.addReminder`) }}
          </app-button>
          </v-col>
        </v-row>
        <v-row class="text--primary">
          <v-col>
            <h4 class="mb-3">
              {{ $t(`${TRANSLATION_KEY_PREFIX}.additionalInfoLabel`) }}
            </h4>
            <div>
              <VisualTextEditor
                v-model:value="additionalInfo"
                counter="footer"
                :placeholder="$t(`${TRANSLATION_KEY_PREFIX}.additionalInfoPlaceholder`)"
                :error-messages="additionalInfoErrors"
                :max-length="ADDITIONAL_INFO_MAX_LENGTH"
              />
            </div>
          </v-col>
        </v-row>
      </template>
    </app-modal>
  </ChecklistTableTaskCellWrapper>
</template>

<script>
import dayjs from 'dayjs'
import { cloneDeep } from 'lodash-es'
import { mapActions, mapGetters } from 'vuex'

import AppDatePicker from '@/common/AppDatePicker.vue'
import AppTextField from '@/common/AppTextField.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import Popover from '@/common/popover/Popover.vue'
import { DATE_FORMAT, ISOToShortenedDate, ISOToYYYYMMDD } from '@/common/utils/dates'
import { escapeHtml , sanitizeString } from '@/common/utils/strings'
import { toast } from '@/common/utils/toast'
import VisualTextEditor from '@/common/visual-text-editor/VisualTextEditor.vue'
import ChecklistTableTaskCellWrapper from '@/project/checklist/table/ChecklistTableTaskCellWrapper.vue'
import ChecklistTableTaskEmptyCell from '@/project/checklist/table/ChecklistTableTaskEmptyCell.vue'
import { EDIT_TASK, GET_TASK_DETAILS } from '@/store/modules/checklist/action_types'




const ADDITIONAL_INFO_MAX_LENGTH = 100
const TRANSLATION_KEY_PREFIX = 'project.checklist.table.ChecklistTableTaskDate'

export default {
  name: 'ChecklistTableTaskDate',
  components: {
    AppDatePicker,
    AppTextField,
    AppTooltip,
    ChecklistTableTaskCellWrapper,
    ChecklistTableTaskEmptyCell,
    Popover,
    VisualTextEditor,
  },
  props: {
    item: {
      type: Object,
      required: true,
    },
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      ADDITIONAL_INFO_MAX_LENGTH,
      TRANSLATION_KEY_PREFIX,
      additionalInfo: '',
      isDialogOpen: false,
      minStartDate: null,
      newDate: null,
      newReminders: [],
      noDate: false,
    }
  },
  computed: {
    ...mapGetters('room', ['isCurrentUserPm']),
    additionalInfoErrors () {
      const errors = []

      if (this.isAdditionalInfoTooLong) {
        errors.push(this.$t('common.validations.textTooLong'))
      }

      return errors
    },
    additionalInfoLength () {
      return escapeHtml(this.additionalInfo).length
    },
    additionalInfoText () {
      return sanitizeString(this.item?.dueDateString)
    },
    computedDate () {
      if (!this.item.dueDate) return null
      return ISOToShortenedDate(this.item.dueDate)
    },
    displayRemindersIcon () {
      return this.item?.reminders?.length > 0 && this.isCurrentUserPm
    },
    displayAdditionalInfo() {
      return !!this.item?.dueDateString
    },
    hasDateRelatedData () {
      return this.computedDate || this.displayAdditionalInfo
    },
    isAdditionalInfoTooLong () {
      return this.additionalInfoLength > ADDITIONAL_INFO_MAX_LENGTH
    },
    isCellClickable () {
      return this.isCurrentUserPm
    },
  },
  created () {
    this.minStartDate = dayjs().format(DATE_FORMAT)
  },
  methods: {
    ...mapActions('checklist', [EDIT_TASK, GET_TASK_DETAILS]),
    async preparePostDate () {
      this.newReminders.forEach(reminder => {
        this.validateReminder(reminder)
      })
      let sentReminders = []
      if (!this.newDate) {
        this.noDate = true
      } else if (this.newDate && this.allRemindersValid()) {
        this.noDate = false
        sentReminders = this.newReminders
      }
      try {
        await this.EDIT_TASK({
          mnemo: this.mnemo,
          taskId: this.item.id,
          data: {
            date: ISOToYYYYMMDD(this.newDate),
            dateString: this.additionalInfo,
            reminders: sentReminders,
          },
        })

        this.GET_TASK_DETAILS({
          mnemo: this.mnemo,
          taskId: this.item.id,
        })

        toast.success(this.$t(`${TRANSLATION_KEY_PREFIX}.postDateSuccess`))
      } catch (error) {
        console.error(error)
        toast.error(this.$t(`${TRANSLATION_KEY_PREFIX}.postDateError`))
      } finally {
        this.resetAndClose()
      }
    },
    allRemindersValid () {
      return this.newReminders.every(reminder => { return !reminder.error })
    },
    resetAndClose () {
      this.isDialogOpen = false
      this.additionalInfo = ''
      this.newDate = null
      this.newReminders = []
      this.noDate = false
    },
    onCellClick () {
      if(!this.isCellClickable) return

      this.newDate = ISOToYYYYMMDD(this.item?.dueDate) || null
      this.newReminders = cloneDeep(this.item?.reminders) || []
      this.additionalInfo = this.item?.dueDateString || ''

      this.isDialogOpen = true
    },
    addNewReminder () {
      this.newReminders.push({
        daysBefore: 0,
        id: null,
        reminderAt: null,
        sent: 0,
      })
    },
    validateReminder (reminder) {
      reminder.errorWrongValue = false
      reminder.errorBackToFuture = false
      reminder.errorAfterEvent = false
      // Check for wrong input value
      if ((!reminder.daysBefore && reminder.daysBefore !== 0) || isNaN(reminder.daysBefore) || reminder.daysBefore === '') reminder.errorWrongValue =  true
      // Check for reminder set after task
      if (reminder.daysBefore && reminder.daysBefore < 0) reminder.errorAfterEvent = true
      // Check for reminder happening in the past
      if (reminder.daysBefore && dayjs().tz('Europe/Paris').set({ hour: 0, minute: 0 }).diff(dayjs(this.newDate).subtract(reminder.daysBefore, 'days'), 'days') > 0) reminder.errorBackToFuture =  true

      reminder.error = (reminder.errorWrongValue || reminder.errorBackToFuture || reminder.errorAfterEvent)
    },
    removeReminder (reminderIndex) {
      this.newReminders.splice(reminderIndex, 1)
    },
  },
}
</script>

<style scoped lang="scss">
@import "vuetify/settings";
@import "@/scss/typography-tokens";

.ChecklistTableTaskDate-due-date {
  display: flex;
  align-items: center;
}

:deep(.ChecklistTableTaskDate-datePicker) {
  .v-input__icon i {
    color: rgb(var(--v-theme-icon-primary)) !important;
    margin-right: $spacer * 4;
    font-size: 16px;
  }
}

:deep(.ChecklistTableTaskDate-dateReminderPicker.v-text-field__suffix) {
  // Space allocated to make the text align even with plurals
  min-width: 75px;
}

.ChecklistTableTaskDate-mobileRowGrid {
  display: grid;
  grid-template-columns: 1fr 2fr;
}
.tooltip-text {
  @include text('body-md-regular');
}

:deep(.editor__footer) {
  display: flex;
  flex-direction: column-reverse;
}

:deep(.editor__content .ProseMirror) {
  max-height: 150px;
}
</style>
